// ReextractLink - inline "Re-extract" text link next to an upload's type, on every
// Files-tab surface (files table, Drive and Gmail trees, failed-extractions banner).
//
// Visible on every row EXCEPT while an extraction is in flight (pending or
// running) - re-extracting then is meaningless and would race it. So it shows on
// succeeded threads too (re-run to pick up a since-improved extractor, mirroring
// the always-on survey link), not just failed ones. Re-runs the extractor against
// the bytes already in S3 (no re-pull from Gmail), the recovery path for a
// transient LLM-output schema deviation or a since-fixed extractor. Once queued,
// the row itself shows the extraction in flight, so the link steps aside.
import { useReextractUpload } from "../api";
import { isExtractionInFlight } from "../utils/extractionInFlight";

interface Props {
  projectId: string;
  uploadId: string;
  extractionStatus: string | undefined;
}

export const ReextractLink = ({
  projectId,
  uploadId,
  extractionStatus,
}: Props): JSX.Element | null => {
  const { reextract, inFlightUploadId, isInFlight, error, clearError } =
    useReextractUpload(projectId);

  const thisInFlight = inFlightUploadId === uploadId;

  // Succeeded / failed / unsupported all show it. While the POST is out the link
  // stays as "Re-extracting…", and a failed POST keeps it visible with its error.
  if (isExtractionInFlight(extractionStatus) && !thisInFlight && !error) {
    return null;
  }

  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "baseline" }}>
      <button
        type="button"
        onClick={() => {
          clearError();
          void reextract(uploadId);
        }}
        disabled={isInFlight}
        style={{
          background: "none",
          border: "none",
          color: isInFlight ? "#9ca3af" : "#2965cc",
          cursor: isInFlight ? "default" : "pointer",
          padding: 0,
          fontSize: 11,
          textDecoration: "underline",
        }}
        title="Re-run extraction on this file (uses the copy already stored - no new fetch)."
      >
        {thisInFlight ? "Re-extracting…" : "Re-extract"}
      </button>
      {error && (
        <span
          role="alert"
          style={{ color: "#b91c1c", fontSize: 11 }}
          title={error}
        >
          re-extract failed
        </span>
      )}
    </span>
  );
};
